import Dexie, { type Table } from 'dexie'
import type { EncryptedBlob } from '../crypto/crypto'
import type { FontCatalogFamily, FontCatalogManifest } from '../../types/fontCatalog'

export interface StoredDocument {
  id: string
  path: string           // Virtual path (e.g., "/documents/Untitled 1.json")
  title: string
  subtype: 'desk' | 'folder' | 'page'
  icon?: string | null
  color?: string | null
  content: string        // JSON AST string (cleared when encrypted at rest)
  format: 'json'
  projectId: string | null
  createdAt: number
  updatedAt: number
  // E2EE at rest fields
  localContentSaved?: boolean
  contentEncrypted?: Uint8Array
  contentNonce?: Uint8Array
  isEncrypted?: boolean
}

export interface StoredSnapshot {
  id: string
  documentId: string
  content: string        // JSON string (cleared when encrypted at rest)
  label: string
  createdAt: number
  wordCount?: number
  // E2EE at rest fields
  contentEncrypted?: Uint8Array
  contentNonce?: Uint8Array
  isEncrypted?: boolean
}

export interface StoredMutation {
  id: string
  type: 'document' | 'project' | 'settings' | 'snapshot'
  action: 'create' | 'update' | 'delete'
  targetId: string
  payload: string
  hlc: string
  timestamp: number
  /**
   * 'pending' items are sent automatically. 'failed' items were refused for a
   * reason a retry will not fix (or failed too many times) and wait for the
   * user; they still count as unsynced.
   */
  status: 'pending' | 'syncing' | 'failed'
  /** When the oldest change merged into this item was queued. */
  firstQueuedAt?: number
  attempts?: number
  lastAttemptAt?: number
  /** Earliest time the automatic flush may try this item again. */
  nextAttemptAt?: number
  lastError?: string
  httpStatus?: number
}

export interface StoredPageDelivery {
  docId: string
  status: 'pending' | 'failed'
  attempts?: number
  nextAttemptAt?: number
  lastError?: string
  httpStatus?: number
}

export interface StoredKeySession {
  offlineUnlock?: import('../crypto/keyManager').PersistedKeyStore['offlineUnlock']
  accountId?: string
  /** Recovery rows retain their own DEKs in the same transaction as their master key. */
  dekCache?: Record<string, { epoch: number; dek: CryptoKey }>
  id: string
  version: number
  mek: CryptoKey
  canary: EncryptedBlob
  /** Cached KDF nonce — see PersistedKeyStore.kdfNonce in keyManager.ts. */
  kdfNonce?: string
}

export interface StoredDekEntry {
  docId: string
  epoch: number
  dekHandle: CryptoKey
}

export interface StoredYjsCacheEntry {
  docId: string
  iv: Uint8Array
  ciphertext: Uint8Array
  seq: number
  updatedAt: number
  /**
   * The cloud log position this cached state is known to contain. Written in
   * the same row as the state so the two can never disagree.
   */
  serverCursor?: YjsServerCursor
}

/** A position in one document's Durable Object update log. */
export interface YjsServerCursor {
  logId: string
  seq: number
}

/**
 * A local Yjs update that has not yet been acknowledged by the Durable Object.
 * The payload is encrypted with the document DEK before it reaches IndexedDB.
 * `batchId` is assigned immediately before transmission and deliberately
 * survives reloads so a retry remains idempotent server-side.
 */
export interface StoredYjsOutboxEntry {
  id: string
  docId: string
  batchId?: string
  epoch: number
  iv: Uint8Array
  ciphertext: Uint8Array
  createdAt: number
}

/**
 * One line of the local sync log. Holds ids, counts and errors only, never
 * text or titles. Repeats of the same event within a minute are merged into
 * one line (see syncLog.ts).
 */
export interface StoredSyncLogEntry {
  id: string
  /** Latest occurrence. */
  at: number
  firstAt: number
  direction: 'out' | 'in' | 'system'
  level: 'info' | 'warn' | 'error'
  kind: string
  message: string
  targetType?: 'page' | 'tree' | 'setting' | 'snapshot'
  targetIds?: string[]
  /** Changes involved, summed over merged repeats. */
  items?: number
  /** Occurrences merged into this line. */
  repeats: number
  httpStatus?: number
}

export interface StoredFontAsset {
  path: string
  bytes: ArrayBuffer
}

export interface StoredFontCatalogCache {
  id: 'manifest'
  manifest: FontCatalogManifest
  etag: string | null
  updatedAt: number
}

export interface StoredFontCatalogFamily {
  offlinePinned?: boolean
  id: string
  family: FontCatalogFamily
  updatedAt: number
}

export class SuperelectricDB extends Dexie {
  documents!: Table<StoredDocument>
  snapshots!: Table<StoredSnapshot>
  outbox!: Table<StoredMutation>
  localIdentity!: Table<{ id: string; accountId: string }>
  credentialTransactions!: Table<{ id: string; metadata: Record<string, string | null>; keys: unknown; certificate: unknown; owner?: string | null }>
  keySession!: Table<StoredKeySession>
  dekCache!: Table<StoredDekEntry>
  yjsCache!: Table<StoredYjsCacheEntry>
  yjsOutbox!: Table<StoredYjsOutboxEntry>
  syncLog!: Table<StoredSyncLogEntry>
  pageDelivery!: Table<StoredPageDelivery>
  offlineAssets!: Table<{ url: string; encrypted: EncryptedBlob; mime: string }>
  fontAssets!: Table<StoredFontAsset>
  fontCatalogCache!: Table<StoredFontCatalogCache>
  installedFontFamilies!: Table<StoredFontCatalogFamily>

  constructor(name = 'superelectric-ink') {
    super(name)
    this.version(1).stores({
      documents: 'id, path, projectId, updatedAt',
      projects:  'id, path, updatedAt',
      snapshots: 'id, documentId, createdAt',
    })
    this.version(2).stores({
      documents: 'id, path, projectId, updatedAt',
      projects:  'id, path, updatedAt',
      snapshots: 'id, documentId, createdAt',
      outbox:    'id, type, targetId, status, timestamp, hlc',
    })
    this.version(3).stores({
      documents: 'id, path, projectId, updatedAt, isEncrypted',
      projects:  'id, path, updatedAt',
      snapshots: 'id, documentId, createdAt, isEncrypted',
      outbox:    'id, type, targetId, status, timestamp, hlc',
    })
    this.version(4).stores({
      documents: 'id, path, subtype, updatedAt, isEncrypted',
      projects: null,
      snapshots: 'id, documentId, createdAt, isEncrypted',
      outbox:    'id, type, targetId, status, timestamp, hlc',
    })
    this.version(5).stores({
      documents: 'id, path, subtype, updatedAt, isEncrypted',
      projects: null,
      snapshots: 'id, documentId, createdAt, isEncrypted',
      outbox:    'id, type, targetId, status, timestamp, hlc',
      keySession: 'id',
      dekCache:   'docId',
    })
    this.version(6).stores({
      documents: 'id, path, subtype, updatedAt, isEncrypted',
      projects: null,
      snapshots: 'id, documentId, createdAt, isEncrypted',
      outbox:    'id, type, targetId, status, timestamp, hlc',
      keySession: 'id',
      dekCache:   'docId',
      yjsCache:   'docId',
    })
    this.version(7).stores({
      documents: 'id, path, subtype, updatedAt, isEncrypted',
      projects: null,
      snapshots: 'id, documentId, createdAt, isEncrypted',
      outbox:    'id, type, targetId, status, timestamp, hlc',
      keySession: 'id',
      dekCache:   'docId',
      yjsCache:   'docId',
      fontAssets: 'path',
      fontCatalogCache: 'id',
      installedFontFamilies: 'id, family.family, updatedAt',
    })
    this.version(8).stores({
      documents: 'id, path, subtype, updatedAt, isEncrypted',
      projects: null,
      snapshots: 'id, documentId, createdAt, isEncrypted',
      outbox:    'id, type, targetId, status, timestamp, hlc',
      keySession: 'id',
      dekCache:   'docId',
      yjsCache:   'docId',
      yjsOutbox:  'id, docId, batchId, createdAt, [docId+createdAt]',
      fontAssets: 'path',
      fontCatalogCache: 'id',
      installedFontFamilies: 'id, family.family, updatedAt',
    })
    this.version(9).stores({
      documents: 'id, path, subtype, updatedAt, isEncrypted',
      projects: null,
      snapshots: 'id, documentId, createdAt, isEncrypted',
      outbox:    'id, type, targetId, status, timestamp, hlc',
      keySession: 'id',
      dekCache:   'docId',
      yjsCache:   'docId',
      yjsOutbox:  'id, docId, batchId, createdAt, [docId+createdAt]',
      syncLog:    'id, at',
      fontAssets: 'path',
      fontCatalogCache: 'id',
      installedFontFamilies: 'id, family.family, updatedAt',
    })
    this.version(10).stores({ credentialTransactions: 'id', localIdentity: 'id' })
    this.version(11).stores({ offlineAssets: 'url' })
    this.version(12).stores({ pageDelivery: 'docId, status, nextAttemptAt' })
  }
}

export const db = new SuperelectricDB()

// ── Feature Detection for Non-Extractable CryptoKey Structured Clone ─────────

let cryptoKeyIdbSupportedCache: boolean | null = null

/**
 * Tests whether the current browser engine reliably supports storing and reading
 * non-extractable CryptoKey objects in IndexedDB via structured clone.
 */
export async function detectCryptoKeyIndexedDbSupport(): Promise<boolean> {
  if (cryptoKeyIdbSupportedCache !== null) {
    return cryptoKeyIdbSupportedCache
  }

  if (typeof window === 'undefined' || !window.indexedDB || !window.crypto?.subtle) {
    cryptoKeyIdbSupportedCache = false
    return false
  }

  try {
    const testKey = await crypto.subtle.generateKey(
      { name: 'AES-GCM', length: 256 },
      false, // non-extractable
      ['encrypt', 'decrypt'],
    )

    // Write to Dexie feature test entry
    await db.keySession.put({
      id: '__feature_test__',
      version: 1,
      mek: testKey,
      canary: { iv: 'test', ciphertext: 'test' },
    })

    // Read back
    const retrieved = await db.keySession.get('__feature_test__')
    await db.keySession.delete('__feature_test__').catch(() => {})

    if (!retrieved || !retrieved.mek || !(retrieved.mek instanceof CryptoKey)) {
      cryptoKeyIdbSupportedCache = false
      return false
    }

    // Trial encrypt to confirm functional CryptoKey handle
    const iv = crypto.getRandomValues(new Uint8Array(12))
    const testPlaintext = new Uint8Array([1, 2, 3, 4])
    const cipherBuf = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      retrieved.mek,
      testPlaintext,
    )

    cryptoKeyIdbSupportedCache = cipherBuf.byteLength > 0
    return cryptoKeyIdbSupportedCache
  } catch (err) {
    console.warn('[WebStorage] Non-extractable CryptoKey in IndexedDB not supported by browser engine:', err)
    cryptoKeyIdbSupportedCache = false
    return false
  }
}

// ── E2EE At-Rest Local Storage Helpers ────────────────────────────────────────

/**
 * Encrypts a document's content at rest using the Master Encryption Key (MEK).
 */
export async function prepareEncryptedDocument(
  doc: StoredDocument,
  masterKey: CryptoKey
): Promise<StoredDocument> {
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const cipherBuf = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    masterKey,
    new TextEncoder().encode(doc.content) as BufferSource
  )

  return {
    ...doc,
    content: '', // Clear plaintext content from storage
    contentEncrypted: new Uint8Array(cipherBuf),
    contentNonce: iv,
    isEncrypted: true,
  }
}

/**
 * Decrypts a document's content at rest if it is encrypted.
 */
export async function readDecryptedDocument(
  doc: StoredDocument,
  masterKey?: CryptoKey | null
): Promise<StoredDocument> {
  if (!doc.isEncrypted || !doc.contentEncrypted || !doc.contentNonce) {
    return doc
  }

  if (!masterKey) {
    throw new Error(`Document ${doc.id} is encrypted at rest, but key session is locked`)
  }

  const plainBuf = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: doc.contentNonce as BufferSource },
    masterKey,
    doc.contentEncrypted as BufferSource
  )

  return {
    ...doc,
    content: new TextDecoder().decode(plainBuf),
  }
}

/**
 * Encrypts a snapshot's content at rest using the Master Encryption Key (MEK).
 */
export async function saveEncryptedSnapshot(
  snapshot: StoredSnapshot,
  masterKey: CryptoKey
): Promise<void> {
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const cipherBuf = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    masterKey,
    new TextEncoder().encode(snapshot.content) as BufferSource
  )

  await db.snapshots.put({
    ...snapshot,
    content: '',
    contentEncrypted: new Uint8Array(cipherBuf),
    contentNonce: iv,
    isEncrypted: true,
  })
}

/**
 * Decrypts a snapshot's content at rest if it is encrypted.
 */
export async function readDecryptedSnapshot(
  snapshot: StoredSnapshot,
  masterKey?: CryptoKey | null
): Promise<StoredSnapshot> {
  if (!snapshot.isEncrypted || !snapshot.contentEncrypted || !snapshot.contentNonce) {
    return snapshot
  }

  if (!masterKey) {
    throw new Error(`Snapshot ${snapshot.id} is encrypted at rest, but key session is locked`)
  }

  const plainBuf = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: snapshot.contentNonce as BufferSource },
    masterKey,
    snapshot.contentEncrypted as BufferSource
  )

  return {
    ...snapshot,
    content: new TextDecoder().decode(plainBuf),
  }
}
